import { useEffect } from 'react';

/**
 * Starts buffering the recipient's attached video as soon as the page opens,
 * long before the reveal animation completes. Uses an offscreen <video> with
 * preload="auto" (reliable across browsers) plus a <link rel="preload"> hint.
 */
export function VideoPreloader({ src, photoUrls }: { src?: string | null; photoUrls?: string[] | null }) {
  useEffect(() => {
    if (!src) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'video';
    link.href = src;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [src]);

  useEffect(() => {
    const photos = (photoUrls ?? []).filter(Boolean);
    if (photos.length === 0) return;
    const links = photos.map((url) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
      const image = new Image();
      image.decoding = 'async';
      image.src = url;
      return link;
    });
    return () => links.forEach((link) => link.remove());
  }, [photoUrls]);

  if (!src) return null;
  return (
    <video
      src={src}
      preload="auto"
      muted
      playsInline
      autoPlay={false}
      onLoadedMetadata={() => console.log('[VideoPreloader] video metadata ready')}
      onCanPlay={() => console.log('[VideoPreloader] video can play')}
      aria-hidden="true"
      tabIndex={-1}
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
        left: -9999,
        top: -9999,
      }}
    />
  );
}
